import get from 'get-value';
import moment from 'moment/min/moment-with-locales';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import 'react-table/react-table.css';
import { debounce } from 'throttle-debounce';
import { fetchAseInternalTicketListPageData } from '../../../actions/ase/actionAseInternalTicketListPage';
import { domains } from '../../../constants/Utils';
import { editAseInternalTicketListButtonVisibility } from '../../../constants/aseInternalTicketRights';
import { ASE_INTERNAL_MAINTENANCE_TICKET_FORM, ASE_INTERNAL_TECHNICAL_BULLETIN_TICKET_FORM, ASE_INTERNAL_TICKET_FORM } from '../../../constants/formConstants';
import listMessages from '../../../intl/afterSales/listMessages';
import { ASE_INTERNAL_MAINTENANCE_TICKET, ASE_INTERNAL_TECHNICAL_BULLETIN_TICKET, ASE_INTERNAL_TICKET } from '../../../routes/paths';
import { formatInternalTicketNumber } from '../../../utils/utils';
import { IconEdit, PrimaryIcon } from '../../common/Button';
import Table from '../../common/table/TicketTable';

class ListAseInternalTicketTable extends React.Component {
    constructor(props) {
        super(props);
        const urlParams = new URLSearchParams(props.location.search);
        const loadedState = this.loadStateFromSessionStorage({
            listInternalAseDefaultPageSize: 10,
            listInternalAseDefaultSort: [{id: 'TICKET_NUMBER', desc: true}],
            listInternalAseDefaultFilters: [],
        });

        if (urlParams.has('status')) {
            this.state = {
                ...loadedState,
                listInternalAseDefaultFilters: [
                    {
                        id: 'STATUS',
                        value: urlParams.get('status') ? urlParams.get('status') : '',
                    }
                ]
            }
        } else {
            this.state = {
                ...loadedState,
                listInternalAseDefaultFilters: loadedState.listInternalAseDefaultFilters.filter(item => item.id !== 'GROUP')
            }
        }

        this.filtering = false;
        this.handleFetchDataDebounced = debounce(1000, this.handleFetchData);
    }

    componentDidMount() {
        window.addEventListener(
            'beforeunload',
            this.saveStateToSessionStorage
        );
    }

    componentWillUnmount() {
        window.removeEventListener(
            'beforeunload',
            this.saveStateToSessionStorage
        );
        this.saveStateToSessionStorage();
    }

    loadStateFromSessionStorage(defaults) {
        return Object.keys(defaults)
            .reduce((acc, key) => {
                if (sessionStorage.hasOwnProperty(key) && key.match(/^listInternalAse*/)) {
                    let value = sessionStorage.getItem(key);
                    value = JSON.parse(value);
                    return {
                        ...acc,
                        [key]: value,
                    };
                }
                return acc;
            }, defaults);
    }

    saveStateToSessionStorage = () => {
        for (let key in this.state) {
            if (this.state.hasOwnProperty(key) && key.match(/^listInternalAse*/)) {
                sessionStorage.setItem(key, JSON.stringify(this.state[key]));
            }
        }
    }

    handleFetchData = state => {
        this.props.fetchAseInternalTicketListPageData(state.pageSize, state.sorted, state.cursor, state.isNextFromPivot, this.normalizeFiltered(state));
        this.filtering = false;
        this.setState({
            listInternalAseDefaultPageSize: state.pageSize,
            listInternalAseDefaultSort: state.sorted,
            listInternalAseDefaultFilters: state.filtered
        });
    };

    fetchStrategy = state => {
        this.filtering ? this.handleFetchDataDebounced(state) : this.handleFetchData(state);
    };

    normalizeFiltered = state => {
        return state.filtered
            .filter(filterItem => filterItem.value)
            .map(filterItem => {
                if (filterItem.value instanceof Date || filterItem.id === 'IT_CREATION_TIME' || filterItem.id === 'LAST_UPDATE') {
                    return {
                        id: filterItem.id,
                        value: moment(filterItem.value).add(12, 'hours').toISOString().substr(0, 10)
                    };
                } else {
                    return filterItem;
                }
            });
    };

    render() {
        const {columns, data, pages, isLoading, handleEditClick, isNextPage, isNextFromPivot, userRoles} = this.props;

        const formTypeToPathMap = {
            [ASE_INTERNAL_TICKET_FORM]: ASE_INTERNAL_TICKET,
            [ASE_INTERNAL_MAINTENANCE_TICKET_FORM]: ASE_INTERNAL_MAINTENANCE_TICKET,
            [ASE_INTERNAL_TECHNICAL_BULLETIN_TICKET_FORM]: ASE_INTERNAL_TECHNICAL_BULLETIN_TICKET,
        };

        return (
            <div className="container-fluid text-center">
                <div className="row">
                    <div className="col">
                        <Table
                            columns={[
                                {
                                    id: 'TICKET_NUMBER',
                                    accessor: 'ticketNumber',
                                    message: listMessages.TABLE_INTERNAL_TICKET_NUMBER,
                                    Cell: row => {
                                        const path = `${formTypeToPathMap[get(row, 'original.formType')] || ASE_INTERNAL_TICKET}/view/${row.original.id}`;
                                        return <Link to={path}>
                                            {get(row, 'original.pfxNumber') ? formatInternalTicketNumber(get(row, 'original.pfxNumber'), get(row, 'original.sfxNumber')) : ''}
                                        </Link>
                                    }
                                },
                                ...columns]}
                            toolButtons={[
                                {
                                    handleClick: handleEditClick,
                                    component: <PrimaryIcon className="btn btn-sm"
                                                            title="Edit"
                                                            type="button"
                                                            key="editButton">
                                        <IconEdit/>
                                    </PrimaryIcon>
                                }
                            ]}
                            isLoading={isLoading}
                            data={data}
                            pages={pages}
                            handleFetchData={this.fetchStrategy}
                            handleOnFilteredChange={() => {
                                this.filtering = true;
                            }}
                            isNextPage={isNextPage}
                            isNextFromPivot={isNextFromPivot}
                            defaultFiltered={this.state.listInternalAseDefaultFilters}
                            defaultSorting={this.state.listInternalAseDefaultSort}
                            defaultPageSize={this.state.listInternalAseDefaultPageSize}
                            buttonVisibility={editAseInternalTicketListButtonVisibility['AS_SG_EDITOR']}
                            sgPermissions={get(userRoles, `${domains.ASE}.sgEditor.sgPermission`)}
                            toolSortable={true}
                        />
                    </div>
                </div>
            </div>
        )
    }
}

ListAseInternalTicketTable.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    data: PropTypes.array.isRequired,
    isNextPage: PropTypes.bool.isRequired,
    isNextFromPivot: PropTypes.bool.isRequired,
    pages: PropTypes.number.isRequired,
    handleEditClick: PropTypes.func.isRequired,
    fetchAseInternalTicketListPageData: PropTypes.func.isRequired,
    columns: PropTypes.array,
    userRoles: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
    isLoading: state.aseInternalTicketListPage.isTableLoading,
    data: state.aseInternalTicketListPage.listData,
    isNextPage: state.aseInternalTicketListPage.isNextPage,
    isNextFromPivot: state.aseInternalTicketListPage.isNextFromPivot,
    pages: state.aseInternalTicketListPage.pages,
    userRoles: state.profile.userDetail.roles,
});

export default connect(mapStateToProps, {
    fetchAseInternalTicketListPageData
})(ListAseInternalTicketTable);
